import React, {useCallback, useEffect, useState} from 'react';
import {AutoSizer, CellMeasurer, List, CellMeasurerCache} from 'react-virtualized';
import styled from 'styled-components';

import {useApi} from '../../lib/api';
import SideModal from '../../components/SideModal';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Loading from '../../components/Loading';
import Notification from '../../components/Notification';
import {groupTemplatesByCategory} from '../../lib/template-library';

import TemplateLibraryCard from './TemplateLibraryCard';

//lets a customer unlock prebuilt form templates with a code, then pick one to deploy into
//their own company. the modal opens on the code prompt instead of a list because templates
//are targeted at specific accounts. skipping the code falls back to the universal templates
function TemplateLibraryModal({open, onClose}) {
  const api = useApi();
  const [cache] = useState(() => new CellMeasurerCache({defaultHeight: 232, fixedWidth: true}));
  const [code, setCode] = useState('');
  const [templates, setTemplates] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [usedCode, setUsedCode] = useState(false);

  //each time the modal opens the customer starts at the code prompt again
  useEffect(() => {
    if (open) {
      setCode('');
      setTemplates(null);
      setFailed(false);
      setUsedCode(false);
    }
  }, [open]);

  const load = useCallback(async (templateCode) => {
    setLoading(true);
    setFailed(false);

    try {
      const result = await api.getLibraryTemplates(templateCode || null);
      cache.clearAll();
      setTemplates(result);
      setUsedCode(!!templateCode);
    }
    catch (e) {
      setFailed(true);
      setTemplates(null);
    }
    finally {
      setLoading(false);
    }
  }, [api, cache]);

  const handleApply = () => load(code.trim());
  const handleSkip = () => load(null);
  const handleBack = () => {
    setTemplates(null);
    setFailed(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && code.trim()) {
      handleApply();
    }
  };

  //flatten the categories so one virtualized list can draw both the headings and the cards
  const rows = groupTemplatesByCategory(templates).reduce((list, group) => [
    ...list,
    {type: 'CATEGORY_TITLE', category: group.category},
    ...group.templates.map(template => ({type: 'TEMPLATE', template}))
  ], []);

  const showingList = templates !== null;

  return (
    <SideModal
      open={open}
      onClose={onClose}
      title="Templates"
      options={showingList ? [<Button secondary onClick={handleBack}>Enter Another Code</Button>] : undefined}>
      {!showingList ? (
        <SideModal.Content>
          <Prompt>
            <Input
              id="template-code"
              label="Enter Code"
              type="text"
              placeholder="Template code"
              value={code}
              disabled={loading}
              onChange={e => setCode(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            <Actions>
              <Button disabled={loading || !code.trim()} onClick={handleApply}>Apply</Button>
              <Button secondary disabled={loading} onClick={handleSkip}>Skip</Button>
            </Actions>
            <Help>
              Enter the code you were given to see the templates prepared for your account. If you do not have a code,
              skip this step to browse the templates available to everyone.
            </Help>
            {loading && <Loading />}
            {failed && (
              <Notification mini error>We could not load templates right now. Please try again.</Notification>
            )}
          </Prompt>
        </SideModal.Content>
      ) : rows.length < 1 ? (
        <SideModal.Content>
          <Notification mini warning>
            {usedCode
              ? 'That code did not match any templates. Check the code and try again, or skip to browse the templates available to everyone.'
              : 'There are no templates available yet.'}
          </Notification>
        </SideModal.Content>
      ) : (
        <AutoSizer>
          {({width, height}) => (
            <List
              height={height}
              width={width}
              style={{overflowX: 'hidden'}}
              rowCount={rows.length}
              rowHeight={cache.rowHeight}
              deferredMeasurementCache={cache}
              overscanRowCount={0}
              rowRenderer={({index, key, parent, style}) => {
                const item = rows[index];

                return (
                  <CellMeasurer cache={cache} columnIndex={0} key={key} parent={parent} rowIndex={index}>
                    <div style={style}>
                      {item.type === 'TEMPLATE' ? (
                        <CardContainer>
                          <TemplateLibraryCard template={item.template} />
                        </CardContainer>
                      ) : (
                        <CategoryHeader>{item.category}</CategoryHeader>
                      )}
                    </div>
                  </CellMeasurer>
                );
              }}
            />
          )}
        </AutoSizer>
      )}
    </SideModal>
  );
}

const Prompt = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.625rem;
`;

const Help = styled.p`
  margin: 0;
  color: #757575;
  font-size: 0.875rem;
`;

const CategoryHeader = styled.h2`
  font-weight: 300;
  margin: 2rem 1.875rem 1.5rem 1.875rem;
`;

const CardContainer = styled.div`
  margin: 1.875rem;
  margin-top: 0;
`;

export default TemplateLibraryModal;
